/*
 * @Description:
 * @Author: Luke Z
 * @Date: 2021-04-08 15:28:01
 * @LastEditors: Jack Deng
 * @LastEditTime: 2021-09-03 11:05:02
 * @FilePath: /unifyxp-v2-store-portal/src/layout/SalesChannelEditorLayout/HomeLayout/SalesChannelHeader.tsx
 */
/**
 *  Header component for sales channel layout.
 *  Author: Chris Hao
 *  CreatedAt: 2021-03-04
 */

import { getI18nFromGeneral } from '@/src/tools/tools';
import { Dropdown, Menu } from 'antd';
import * as CSS from 'csstype';
import React from 'react';

import Button from '@/src/components/Antd/Button/Button';
import MultiLangDropDown from '@/src/constrains/SalesChannelMultiLanguageSelect/MultiLangDropDown';
import SalesChannelStatusIcon from '@/src/layout/Components/SalesChannel/SalesChannelStatusIcon';
import { Icon } from '@/src/tools/icon';
import { SalesChannelStatus } from '@/src/types/common/SalesChannel';
import { LeftOutlined } from '@ant-design/icons';
import GlobalTooltip, {
  NoWrapContainer,
} from 'src/components/GlobalTooltip/GlobalTooltip';
import EditingUsers from 'src/layout/SalesChannelEditorLayout/HomeLayout/EditingUsers';

import { EnumMultiLanguageType } from '@/src/constrains/SalesChannelMultiLanguageSelect';
import SiteStyleSelect from 'src/constrains/SalesChannelStyleSelect/WebSiteTypeSelect';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import css from './SalesChannelHeader.less';

interface IProps {
  style?: CSS.Properties;
  title: string | JSX.Element;
  description: string | JSX.Element;
  status?: SalesChannelStatus;
  disabledDelete?: boolean;
  hasModified: boolean;
  showVisibility?: boolean;
  visible?: boolean;
  onChangeVisibility?: (visible: boolean) => void;
  submit: () => void;
  onDiscard: () => void;
  onGoBack: () => void;
  onDelete?: () => void;
  hideMultiLanguage?: boolean;
  salesChannelUuid?: string;
  customType?: EnumMultiLanguageType;
  rarePart?;
  hasStyleSelect?: boolean;
  submitLoading?: boolean;
  onContentReverse?: any;
}

const SalesChannelHeader = ({
  style = {},
  title,
  description,
  status,
  hasModified,
  //showVisibility,
  visible,
  disabledDelete,
  submit,
  onDiscard,
  onGoBack,
  onDelete,
  onChangeVisibility,
  customType,
  hideMultiLanguage,
  salesChannelUuid,
  rarePart,
  hasStyleSelect,
  submitLoading,
  onContentReverse,
}: IProps) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  //console.log('hideMultiLanguage', hideMultiLanguage);
  const handleClickGoBack = () => {
    if (onGoBack) {
      onGoBack();
    }
  };

  const handleClickDiscard = () => {
    if (onDiscard) {
      onDiscard();
    }
  };

  const handleClickSave = () => {
    if (hasModified && submit && !submitLoading) {
      submit();
    }
  };

  const handleMenuClick = ({ key }) => {
    if (key === 'visible' && !visible) onChangeVisibility(true);
    if (key === 'invisible' && visible) onChangeVisibility(false);
  };

  return (
    <div className={css.container} style={{ ...style }}>
      <Button
        onClick={handleClickGoBack}
        icon={<LeftOutlined rev={'rev'} />}
        className={css.left}>
        {getI18n('general.back')}
      </Button>
      <div className={css.center}>
        <div className={css.centerTitle}>{title}</div>
        <div className={css.centerBody}>
          {status && <SalesChannelStatusIcon status={status} />}
          <NoWrapContainer className={css.centerDescription}>
            <GlobalTooltip title={description}>{description}</GlobalTooltip>
          </NoWrapContainer>
        </div>
      </div>
      <div className={css.right}>
        <EditingUsers />
        {hasModified && (
          <div className={css.unsavedChangeText}>
            {getI18nFromGeneral('general.commonHeader.leave_header')}
          </div>
        )}

        {hasStyleSelect && (
          <SiteStyleSelect
            salesChannelUuidKey={'saleChannelUuid'}
            customType={customType}
            hasModified={hasModified}
          />
        )}
        {!hideMultiLanguage && (
          <MultiLangDropDown
            salesChannelUuidKey={'saleChannelUuid'}
            customType={customType}
            hasModified={hasModified}
          />
        )}

        {onChangeVisibility && (
          <Dropdown
            getPopupContainer={() => document.body}
            overlay={
              <Menu onClick={handleMenuClick}>
                <Menu.Item key={'visible'}>
                  {getI18n('general.visible')}
                </Menu.Item>
                <Menu.Item key='invisible'>
                  {getI18n('general.invisible')}
                </Menu.Item>
              </Menu>
            }
            placement='bottomCenter'>
            <div className={css.dropdownButton}>
              <div className={css.dropdownLeft}>
                {visible ? (
                  <span style={{ color: '#38C568' }}>
                    <Icon type={'iconvisible'} className={css.visible} />
                    {getI18n('general.visible')}
                  </span>
                ) : (
                  <span style={{ color: '#5C6269' }}>
                    <Icon type={'iconinvisible'} className={css.visible} />
                    {getI18n('general.invisible')}
                  </span>
                )}
              </div>
              <div className={css.dropdownRight}>
                <Icon type='icondown' className={css.icon} />
              </div>
            </div>
          </Dropdown>
        )}

        {onDiscard && (
          <Button className={css.rightDiscard} onClick={handleClickDiscard}>
            {getI18n('general.discard')}
          </Button>
        )}

        {submit && (
          <Button
            disabled={!hasModified || submitLoading}
            className={css.rightSave}
            loading={submitLoading}
            type='primary'
            onClick={handleClickSave}>
            {getI18n('general.save')}
          </Button>
        )}

        {onDelete && (
          <Button
            type='danger'
            ghost
            disabled={disabledDelete}
            icon={<Icon type='icondelete' className={css.icon} />}
            onClick={onDelete}>
            {getI18n('general.delete')}
          </Button>
        )}
      </div>
      {rarePart || null}
    </div>
  );
};

export default SalesChannelHeader;
